import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, RefreshCw, Bookmark, Newspaper, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_STYLES = {
  Mercado: 'bg-amber-500/15 text-amber-700 border-amber-500/30',
  Regulação: 'bg-rose-500/15 text-rose-700 border-rose-500/30',
  Tecnologia: 'bg-sky-500/15 text-sky-700 border-sky-500/30',
  Institucional: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  Mineração: 'bg-violet-500/15 text-violet-700 border-violet-500/30',
  DeFi: 'bg-teal-500/15 text-teal-700 border-teal-500/30',
  Outras: 'bg-slate-500/15 text-slate-700 border-slate-500/30',
};

const SORT_OPTIONS = [
  { value: 'recent', label: 'Mais recentes' },
  { value: 'popular', label: 'Mais lidas' },
];

function formatRelativeTime(dateString) {
  if (!dateString) return 'Agora';
  const date = new Date(dateString);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'Agora mesmo';
  if (diffMin < 60) return `${diffMin} min atrás`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h atrás`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD}d atrás`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function CategoryBadge({ category }) {
  const style = CATEGORY_STYLES[category] || CATEGORY_STYLES.Outras;
  return (
    <span
      className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${style}`}
      data-testid="news-category-badge"
    >
      {category}
    </span>
  );
}

function NewsCard({ item, saved, onToggleSave, onSelect }) {
  const category = item.subcategory || 'Outras';
  const fallbackImage = 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900';
  const image = item.featured_image || fallbackImage;

  return (
    <article
      onClick={() => onSelect(item)}
      className="group cursor-pointer bg-card rounded-2xl overflow-hidden border border-border hover:border-primary/40 hover:shadow-lg transition-all duration-300"
      data-testid={`news-card-${item.slug}`}
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-muted">
        <img
          src={image}
          alt={item.image_alt || item.title}
          onError={(e) => { e.currentTarget.src = fallbackImage; }}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3">
          <CategoryBadge category={category} />
        </div>
      </div>

      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-bold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
            {item.title}
          </h3>
          <button
            onClick={(event) => {
              event.stopPropagation();
              onToggleSave(item.id);
            }}
            className={`shrink-0 p-1.5 rounded-full transition-colors ${
              saved ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
            }`}
            aria-label={saved ? 'Remover dos favoritos' : 'Salvar'}
            data-testid={`news-save-${item.slug}`}
          >
            <Bookmark size={16} fill={saved ? 'currentColor' : 'none'} />
          </button>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
          {item.excerpt}
        </p>

        <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground border-t border-border/50">
          <span className="flex items-center gap-1.5">
            <Clock size={12} /> {formatRelativeTime(item.created_at)}
          </span>
          {item.source_name && (
            <span className="flex items-center gap-1 truncate max-w-[60%]" title={item.source_name}>
              <Newspaper size={12} /> {item.source_name}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-card rounded-2xl overflow-hidden border border-border animate-pulse" data-testid="news-skeleton">
      <div className="aspect-[16/9] bg-muted" />
      <div className="p-5 space-y-3">
        <div className="h-4 bg-muted rounded w-3/4" />
        <div className="h-4 bg-muted rounded w-full" />
        <div className="h-3 bg-muted rounded w-1/2" />
      </div>
    </div>
  );
}

export default function BitcoinNews() {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('recent');
  const [saved, setSaved] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('btc-atlas-saved-news') || '[]');
    } catch {
      return [];
    }
  });

  const fetchNews = useCallback(async () => {
    setError(null);
    try {
      const response = await axios.get(`${API}/articles?category=news&limit=20`);
      setNews(response.data || []);
    } catch (e) {
      console.error('Failed to fetch news:', e);
      setError('Não foi possível carregar as notícias agora. Tente novamente em instantes.');
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchNews();
      setLoading(false);
    })();
  }, [fetchNews]);

  useEffect(() => {
    localStorage.setItem('btc-atlas-saved-news', JSON.stringify(saved));
  }, [saved]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/admin/force-update?max_news=6`);
    } catch (e) {
      console.warn('Force-update failed (ok if rate-limited):', e?.message);
    }
    await fetchNews();
    setRefreshing(false);
  };

  const sorted = [...news].sort((a, b) => {
    if (sort === 'popular') return (b.views || 0) - (a.views || 0);
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const toggleSave = (id) =>
    setSaved((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const openArticle = (article) => {
    const slug = article.slug || article.id;
    navigate(`/noticias/${slug}`);
  };

  return (
    <div className="space-y-6" data-testid="bitcoin-news-container">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
        <div className="flex items-center gap-2" data-testid="news-sort-tabs">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSort(opt.value)}
              className={`text-sm font-medium px-3 py-1.5 rounded-full transition-colors ${
                sort === opt.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
              }`}
              data-testid={`news-sort-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          variant="outline"
          size="sm"
          className="gap-2"
          data-testid="news-refresh-button"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Buscando...' : 'Atualizar'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div
          className="flex items-start gap-3 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800"
          data-testid="news-error"
        >
          <AlertCircle size={20} className="shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold mb-1">Erro ao carregar notícias</p>
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
        ) : sorted.length === 0 ? (
          <div
            className="col-span-full text-center py-16 px-6 rounded-2xl border border-dashed border-border bg-muted/30"
            data-testid="news-empty-state"
          >
            <Newspaper className="mx-auto mb-4 text-muted-foreground" size={48} />
            <h3 className="text-lg font-semibold mb-2">Nenhuma notícia disponível ainda</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Estamos preparando as últimas notícias sobre Bitcoin para você. Clique em &ldquo;Atualizar&rdquo; para forçar uma nova busca.
            </p>
            <Button onClick={handleRefresh} disabled={refreshing} data-testid="news-empty-refresh">
              <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Buscar agora
            </Button>
          </div>
        ) : (
          sorted.map((item) => (
            <NewsCard
              key={item.id}
              item={item}
              saved={saved.includes(item.id)}
              onToggleSave={toggleSave}
              onSelect={openArticle}
            />
          ))
        )}
      </div>

      {!loading && sorted.length > 0 && (
        <p
          className="text-center text-xs text-muted-foreground pt-4"
          data-testid="news-count"
        >
          {sorted.length} {sorted.length === 1 ? 'notícia carregada' : 'notícias carregadas'} ·
          Atualizado a cada 3 horas pelo Bitcoin Atlas Bot
        </p>
      )}
    </div>
  );
}
